import { useEffect, useMemo } from 'react';

import { QueryHookOptions, useQuery } from '@apollo/react-hooks';
import { isNil } from 'lodash-es';

import { IServerOrder } from '@rbi-ctg/menu';
import { DeliveryStatus } from 'generated/rbi-graphql';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import {
  GetOrderQuery,
  IGetOrderQueryResponse,
  IGetOrderQueryVariables,
} from 'remote/queries/order';
import { OrderStatus } from 'state/order';
import { orderPollFailure, orderPollSuccessful } from 'state/order/order-state-utils';
import noop from 'utils/noop';

import { handleOrderStatusChange } from './on-order-status-change';
import { IUseOrderStatusParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export const useOrderStatus = ({
  failureStatuses,
  logOrderLatency,
  logger,
  onSuccess = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  successStatuses,
}: IUseOrderStatusParams) => {
  const queryParams: QueryHookOptions<IGetOrderQueryResponse, IGetOrderQueryVariables> = {
    pollInterval: orderStatusPollInterval,
    skip: isNil(rbiOrderId),
  };

  if (rbiOrderId) {
    queryParams.variables = { rbiOrderId };
  }

  const queryResult = useQuery<IGetOrderQueryResponse, IGetOrderQueryVariables>(
    GetOrderQuery,
    queryParams
  );

  const { data, stopPolling } = queryResult;

  const serverOrder = useMemo<IServerOrder | null>(() => (data && data.order ? data.order : null), [
    data,
  ]);

  const orderStatus = serverOrder && serverOrder.status;
  const orderDeliveryStatus: DeliveryStatus | null =
    serverOrder && serverOrder.delivery && serverOrder.delivery.status;

  const prevOrderStatus = usePrevious<OrderStatus | null>(orderStatus);
  const prevOrderDeliveryStatus = usePrevious<DeliveryStatus | null>(orderDeliveryStatus);

  const failure =
    !!serverOrder &&
    orderPollFailure({
      cateringFailureStatus: failureStatuses.catering,
      deliveryFailureStatus: failureStatuses.delivery,
      order: serverOrder,
      orderFailureStatus: failureStatuses.pos,
    });

  // update with catering
  const success =
    !!serverOrder &&
    orderPollSuccessful({
      cateringSuccessStatus: successStatuses.catering,
      deliverySuccessStatus: successStatuses.delivery,
      order: serverOrder,
      orderSuccessStatus: successStatuses.pos,
    });

  useEffect(() => {
    if (!rbiOrderId || success || failure) {
      stopPolling();
    }
  }, [rbiOrderId, stopPolling, success, failure]);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    handleOrderStatusChange({
      failure,
      logOrderLatency,
      logger,
      onSuccess,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      success,
    });
  }, [
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
  ]);

  useEffectOnUnmount(() => {
    stopPolling();
  });

  return {
    ...queryResult,
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
  };
};
